import { axiosGroup } from "./axiosGroup";

export type LaunchpadPurchaseStatus =
  | "pending"
  | "completed"
  | "failed"
  | "escrowed"
  | "released"
  | "refunded";

/**
 * Real settlement state derived from the vault deposit. Present only for
 * vault-backed purchases — `transactions.status` alone never advances past
 * "escrowed" once the vault holds the funds.
 */
export type SettlementStatus =
  | "escrowed"
  | "release_pending"
  | "released"
  | "refund_pending"
  | "refunded";

export interface LaunchpadPurchase {
  transactionId: string;
  createdAt: string | null;
  collectionId: string | null;
  collectionName: string | null;
  collectionImage: string | null;
  buyerEmail: string | null;
  buyerWalletAddress: string | null;
  quantity: number;
  pricePerNft: number;
  totalPrice: number;
  sellerAmount: number;
  platformFee: number;
  currencySymbol: string;
  paymentMethod: string | null;
  status: LaunchpadPurchaseStatus | null;
  settlementStatus: SettlementStatus | null;
  releaseTxHash: string | null;
  refundTxHash: string | null;
  successStatus: boolean | null;
  txHash: string | null;
}

export interface CurrencyTotals {
  count: number;
  totalAmount: number;
  totalSellerAmount: number;
  totalPlatformFee: number;
}

export interface LaunchpadPurchaseSummary {
  count: number;
  byStatus: Record<string, number>;
  /** Amounts stay grouped by currency — TAL and USDT must never be summed. */
  byCurrency: Record<string, CurrencyTotals>;
}

export interface LaunchpadPurchaseResponse {
  data: LaunchpadPurchase[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
  summary: LaunchpadPurchaseSummary;
  truncated: boolean;
}

export interface LaunchpadPurchaseFilters {
  page?: number;
  limit?: number;
  from?: string;
  to?: string;
  status?: LaunchpadPurchaseStatus | "";
  search?: string;
}

export const getLaunchpadPurchasesAPI = async (
  filters: LaunchpadPurchaseFilters = {}
): Promise<LaunchpadPurchaseResponse> => {
  const params = new URLSearchParams();
  if (filters.page) params.set("page", String(filters.page));
  if (filters.limit) params.set("limit", String(filters.limit));
  if (filters.from) params.set("from", filters.from);
  if (filters.to) params.set("to", filters.to);
  if (filters.status) params.set("status", filters.status);
  if (filters.search) params.set("search", filters.search);

  const result = await axiosGroup.axiosServerAPI.get(
    `/admin/transactions/launchpad-purchases?${params.toString()}`
  );
  return result.data;
};
